'use client';
import React, { ChangeEvent, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import { setActiveCourseData } from '@/lib/slices/course-slice';
import { setCreateQuizActive, setIsEditingQuiz } from '@/lib/slices/quiz-ui-slice';
import { resetQuizData, setQuizName } from '@/lib/slices/quiz-slice';
import { setUnitId } from '@/lib/slices/unit-slice';
import { setSubunitId } from '@/lib/slices/subunit-slice';
import { setActivityIndex, resetActivitySlice } from '@/lib/slices/activity-slice';

import ModalComponent from '@/common/components/modals/ModalComponent';
import Button from '@/common/components/ui/Button';
import StandardInputText from '@/common/components/input/StandardInputText';
import StandardInputTextarea from '@/common/components/input/StandardInputTextarea';
import StandardInputFile from '@/common/components/input/StandardInputFile';
import StandardInputSelect from '@/common/components/input/StandardInputSelect';
import StandardInputCheckbox from '@/common/components/input/StandardInputCheckbox';
import { AddIcon, AIIcon, CancelIcon, SaveIcon } from '@/assets/icons/button';

import Request from '@/services/util/request-service';
import ActivityCreationService from '@/services/course/creation/activity-creation-service';
import { ActivityBuilderData, ActivityData, CourseData } from 'course-types';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import { toDate } from 'date-fns';

function ContentCreatorWizard() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  // Redux state
  const modalVisible = useAppSelector(() => store.getState().modal.visible);
  const modalName = useAppSelector(() => store.getState().modal.name);
  const activityIndex = useAppSelector(() => store.getState().activity.index);
  const activeSubunitId = useAppSelector(() => store.getState().subunit.id);
  const activeUnitId = useAppSelector(() => store.getState().unit.id);
  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  // Content form state
  const [contentType, setContentType] = useState<
    'document' | 'video' | 'quiz' | 'resource' | 'assignment'
  >('document');
  const [contentName, setContentName] = useState<string>('');
  const [contentUrl, setContentUrl] = useState<string>('');
  const [contentDesc, setContentDesc] = useState<string>('');
  const [contentFile, setContentFile] = useState<any>(null);
  const [isTypeLocked, setIsTypeLocked] = useState<boolean>(false);

  // Assignment-specific state
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [lockAssignment, setLockAssignment] = useState<boolean>(false);
  const [blocksDownstreamContent, setBlocksDownstreamContent] = useState<boolean>(false);

  // Loading states
  const [isLoadingName, setIsLoadingName] = useState<boolean>(false);
  const [isLoadingDesc, setIsLoadingDesc] = useState<boolean>(false);
  const [adjustHover, setAdjustHover] = useState<boolean>(false);

  // Effects
  useEffect(() => {
    if (modalVisible) determineIsTypeLocked();
  }, [activeUnitId, activeSubunitId, activityIndex, modalVisible]);

  // Handlers
  const handleClose = () => {
    setContentType('document');
    setContentName('');
    setContentDesc('');
    setContentUrl('');
    setContentFile(null);

    // Reset assignment-specific state
    setDueDate(null);
    setLockAssignment(false);
    setBlocksDownstreamContent(false);

    // Clear activity state to ensure TopNav shows course title on course pages
    dispatch(resetActivitySlice());

    dispatch(setModalVisible(false));
    dispatch(setModalName(''));
  };

  const handleDescChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= 250) {
      setContentDesc(e.target.value);
    }
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setContentFile(e.target.files[0]);
    }
  };

  const formatDate = (dateInput: Date | null): string => {
    if (!dateInput) return '';
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput as unknown as string);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const handleSubmit = () => {
    if (contentType === 'quiz') {
      // Set up quiz creation context
      dispatch(setUnitId(activeUnitId));
      dispatch(setSubunitId(activeSubunitId));
      dispatch(setActivityIndex(activityIndex));

      // Initialize quiz data
      dispatch(resetQuizData());
      dispatch(setQuizName(contentName || 'Untitled Quiz'));

      // Open quiz editor
      dispatch(setCreateQuizActive(true));
      dispatch(setIsEditingQuiz(false));

      handleClose();
    } else {
      onSaveContent({
        type: contentType,
        name: contentName,
        description: contentDesc,
        url: contentUrl,
        file: contentFile,
        due_date: dueDate,
        lock_after_due_date: lockAssignment,
        blocks_downstream_content: blocksDownstreamContent,
      });

      handleClose();
    }
  };

  // Content management
  const determineIsTypeLocked = () => {
    const alreadyExists: boolean =
      activityIndex > -1 &&
      activityIndex <
        (activeSubunitId != null
          ? (
              (activeCourseData.units[activeUnitId]?.subunits ?? {})[activeSubunitId]?.activities ??
              []
            ).length
          : Object.keys(activeCourseData.units[activeUnitId]?.subunits ?? {}).length +
            (activeCourseData.units[activeUnitId]?.activities ?? []).length);

    const currentList =
      activeSubunitId != null
        ? ((activeCourseData.units[activeUnitId]?.subunits ?? {})[activeSubunitId]?.activities ??
          [])
        : (activeCourseData.units[activeUnitId]?.activities ?? []);
    const currentIndex = currentList.indexOf(
      currentList.find((activity) => activity.index == activityIndex) as ActivityData,
    );

    const activity: ActivityData =
      alreadyExists && currentIndex != -1
        ? currentList[currentIndex]
        : { id: '', name: '', url: '', description: '', type: 'document' };

    setIsTypeLocked(alreadyExists);
    setContentType(activity.type);
    setContentName((activity.name ?? '').trim());
    setContentDesc((activity.description ?? '').trim());
    setContentUrl(activity.url?.trim() ?? '');

    // Load assignment-specific data if editing an assignment
    if (activity.type === 'assignment' && alreadyExists && currentIndex != -1) {
      setDueDate(activity.due_date ? toDate(activity.due_date) : null);
      setLockAssignment(activity.lock_after_due_date || false);
      setBlocksDownstreamContent(activity.blocks_downstream_content || false);
    } else {
      // Reset assignment-specific data for other content types
      setDueDate(null);
      setLockAssignment(false);
      setBlocksDownstreamContent(false);
    }
  };

  const onSaveContent = async (activityData: ActivityBuilderData) => {
    try {
      const currentMaxIndex =
        activeSubunitId != null
          ? (activeCourseData.units[activeUnitId]?.subunits[activeSubunitId]?.activities ?? [])
              .length
          : (activeCourseData.units[activeUnitId]?.activities ?? []).length +
            Object.keys(activeCourseData.units[activeUnitId]?.subunits).length;

      let newActivityList: ActivityData[] | null = null;
      if (currentMaxIndex <= activityIndex) {
        // if new content added then recalculate prerequistes by fetching course structure
        const courseData = await addNewContent(activityData);
        if (courseData) {
          dispatch(setActiveCourseData(courseData));
        }
        toast.success(`New ${activityData.type} added`);
        return;
      } else {
        newActivityList = await updateExistingContent(activityData);
        toast.success(`Updated ${activityData.type} successfully`);
        if (!newActivityList) {
          return;
        }
      }

      if (activeSubunitId != null) {
        const updatedCourse: CourseData = {
          ...activeCourseData,
          units: {
            ...activeCourseData.units,
            [activeUnitId]: {
              ...activeCourseData.units[activeUnitId],
              subunits: {
                ...activeCourseData.units[activeUnitId].subunits,
                [activeSubunitId]: {
                  ...activeCourseData.units[activeUnitId].subunits[activeSubunitId],
                  activities: newActivityList,
                },
              },
            },
          },
        };

        dispatch(setActiveCourseData(updatedCourse));
      } else {
        const updatedCourse: CourseData = {
          ...activeCourseData,
          units: {
            ...activeCourseData.units,
            [activeUnitId]: {
              ...activeCourseData.units[activeUnitId],
              activities: newActivityList,
            },
          },
        };

        dispatch(setActiveCourseData(updatedCourse));
      }
    } catch (error) {
      toast.error('Failed to save content for course');
    }

    dispatch(setModalVisible(false));
    dispatch(setModalName(''));
  };

  const addNewContent = async (activityData: ActivityBuilderData) => {
    let endpointUrl = '';
    let createResponse;

    switch (activityData.type) {
      case 'document':
        endpointUrl = 'upload/document';
        break;
      case 'video':
        endpointUrl = 'content/video';
        break;
      case 'resource':
        endpointUrl = 'content/resource';
        break;
      case 'assignment':
        endpointUrl = 'assignment';
        break;
      default:
        endpointUrl = '';
        break;
    }

    if (endpointUrl == '') {
      throw Error('Invalid content type.');
    }

    if (activityData.type == 'document' || activityData.type == 'assignment') {
      const formBody: FormData = new FormData();
      formBody.append('name', activityData.name);
      formBody.append('index', `${activityIndex}`);
      if (activityData.description) {
        formBody.append('description', activityData.description);
      }
      if (activeSubunitId != null) {
        formBody.append('subunitId', activeSubunitId);
      } else {
        formBody.append('unitId', activeUnitId);
      }
      if (activityData.due_date) {
        formBody.append('due_date', activityData.due_date.toISOString());
      }
      if (activityData.lock_after_due_date) {
        formBody.append('lock_after_due_date', activityData.lock_after_due_date.toString());
      }
      if (activityData.blocks_downstream_content) {
        formBody.append(
          'blocks_downstream_content',
          activityData.blocks_downstream_content.toString(),
        );
      }
      formBody.append('file', activityData.file);
      createResponse = await (await Request.post(endpointUrl, formBody))?.json();
    } else {
      const contentData: any = {
        name: activityData.name,
        subunitId: activeSubunitId,
        index: activityIndex,
      };
      if (activeSubunitId != null) {
        contentData.subunitId = activeSubunitId;
      } else {
        contentData.unitId = activeUnitId;
      }

      contentData.description = activityData.description;
      if (activityData.type == 'resource') {
        contentData.url = activityData.url;
      }

      if (activityData.type == 'video') {
        contentData.url = ActivityCreationService.reformatVideoURL(activityData.url ?? '');
      }
      createResponse = await (await Request.post(endpointUrl, contentData))?.json();
    }

    if (createResponse.error != undefined) {
      throw Error(`Failed to add ${activityData.type}`);
    }

    createResponse.content.type = activityData.type;

    const courseData = await UserCourseStorageService.setCourseData(activeCourseData.id);
    return courseData;
  };

  const updateExistingContent = async (
    activityData: ActivityBuilderData,
  ): Promise<ActivityData[] | null> => {
    let endpointUrl = '';

    switch (activityData.type) {
      case 'document':
        endpointUrl = 'content/document/update';
        break;
      case 'video':
        endpointUrl = 'content/video/update';
        break;
      case 'resource':
        endpointUrl = 'content/resource/update';
        break;
      case 'assignment':
        endpointUrl = 'assignment/update';
        break;
      default:
        endpointUrl = '';
        break;
    }

    if (endpointUrl == '') {
      throw Error('Invalid content type.');
    }

    const contentData: any = {
      name: activityData.name,
      description: activityData.description,
      index: activityIndex,
    };

    if (activeSubunitId != null) {
      contentData.id = (
        (activeCourseData.units[activeUnitId]?.subunits ?? {})[activeSubunitId]?.activities[
          activityIndex
        ] as ActivityData
      ).id;
      contentData.subunitId = activeSubunitId;
    } else {
      contentData.id = (
        activeCourseData.units[activeUnitId].activities.find(
          (activity) => activity.index == activityIndex && activity.type == activityData.type,
        ) as ActivityData
      ).id;
      contentData.unitId = activeUnitId;
    }

    activityData.type == 'resource' ? (contentData.url = activityData.url) : '';
    activityData.type == 'video'
      ? (contentData.url = ActivityCreationService.reformatVideoURL(activityData.url ?? ''))
      : '';
    if (activityData.type == 'assignment') {
      contentData.due_date = activityData.due_date;
      contentData.lock_after_due_date = activityData.lock_after_due_date;
      contentData.blocks_downstream_content = activityData.blocks_downstream_content;
    }

    const createResponse = await (await Request.post(endpointUrl, contentData))?.json();

    if (createResponse.error != undefined) {
      throw Error(`Failed to update ${activityData.type}`);
    }

    if (activityData.type == 'assignment') {
      const courseData = await UserCourseStorageService.setCourseData(activeCourseData.id);
      dispatch(setActiveCourseData(courseData));
      return null;
    }

    createResponse.content.type = activityData.type;

    const currentList =
      activeSubunitId != null
        ? (activeCourseData.units[activeUnitId]?.subunits ?? {})[activeSubunitId]?.activities
        : (activeCourseData.units[activeUnitId]?.activities ?? []);
    const currentIndex =
      currentList.indexOf(
        currentList.find((activity) => activity.index == activityIndex) as ActivityData,
      ) ?? 0;

    return [
      ...currentList.slice(0, currentIndex),
      createResponse.content as ActivityData,
      ...currentList.slice(currentIndex + 1),
    ];
  };

  const adjustContentDetails = async (name: boolean, desc: boolean) => {
    // Abort if neither name nor description are selected for adjustment
    if (!name && !desc) return;

    // Lock relevant input areas being generated
    if (name) setIsLoadingName(true);
    if (desc) setIsLoadingDesc(true);

    // Helper for unlocking input fields
    const unlockContentLoading = (name: boolean, desc: boolean) => {
      if (name) setIsLoadingName(false);
      if (desc) setIsLoadingDesc(false);
    };

    try {
      // Retrieve data from aurora
      const res = await Request.post(
        'course/content/enhance',
        { Title: contentName, Description: contentDesc, ChangeTitle: name, ChangeDesc: desc },
        true,
      );
      const body = await res?.json();

      // Handle errors with request / response
      if (!res || !body) {
        throw new Error('Error while communicating with the server');
      }

      // Ensure relevant data exists in the json response
      if (
        (name && typeof body.title !== 'string') ||
        (desc && typeof body.description !== 'string')
      ) {
        throw new Error('Invalid response format from server');
      }

      // Update relevant state values
      if (name) setContentName(body.title);
      if (desc) setContentDesc(body.description);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Something went wrong during enhancement',
      );
    } finally {
      // Always unlock modal inputs for editing
      unlockContentLoading(name, desc);
    }
  };

  return (
    <ModalComponent
      isOpen={modalVisible && modalName == 'AddNewContent'}
      onRequestClose={handleClose}
      closeOnClickOff={true}
      contentLabel={`${isTypeLocked ? 'Edit' : 'Add'} Content`}
      className='min-w-[40vw] max-w-[900px] w-full'
    >
      <div className='relative w-full'>
        <div className='flex flex-col gap-[16px] w-full'>
          <div className='mb-14'>
            <StandardInputSelect
              id='contentType'
              label='Content Type'
              isRequired={true}
              value={contentType}
              onChange={(e) =>
                setContentType(
                  e.target.value as 'document' | 'video' | 'quiz' | 'resource' | 'assignment',
                )
              }
              disabled={isTypeLocked}
              options={[
                { value: 'document', label: 'Document' },
                { value: 'video', label: 'Video' },
                { value: 'quiz', label: 'Quiz' },
                { value: 'resource', label: 'Resource' },
                { value: 'assignment', label: 'Assignment' },
              ]}
            />
          </div>

          <div
            className='relative ml-auto w-fit h-10'
            onMouseEnter={() => setAdjustHover(true)}
            onMouseLeave={() => setAdjustHover(false)}
          >
            <Button
              // outline={true}
              onClick={() => adjustContentDetails(true, true)}
              disabled={
                (contentName === '' && contentDesc === '') || isLoadingName || isLoadingDesc
              }
              className='w-fit h-10'
              analyticsFeature='Add Content'
              analyticsPage='Courses'
            >
              <AIIcon className='w-[20px] h-[20px] mr-2' /> {`Adjust`}
            </Button>
            {adjustHover && (
              <div className='absolute text-center z-20 flex w-[100vw] max-w-[15vw] text-wrap bg-surface_gray_200 rounded-lg px-2 py-1 top-12 -left-3/4 shadow-sm'>
                Automatically generate a name from a description, a description from a name, or
                enhance the overall wording and clarity of existing details.
              </div>
            )}
          </div>

          <div className='flex flex-col w-full'>
            <div className='mb-4 flex flex-col w-full relative'>
              <div className='w-full flex flex-col relative'>
                <StandardInputText
                  id='contentName'
                  label='Content Name'
                  isRequired={true}
                  value={contentName}
                  onChange={(e) => setContentName(e.target.value)}
                  disabled={isLoadingName}
                  isLoading={isLoadingName}
                />
                <div className='absolute top-[32px] right-0 z-10 flex items-center justify-center pointer-events-none'>
                  <Button
                    text={true}
                    onClick={() => adjustContentDetails(true, false)}
                    disabled={
                      (contentName === '' && contentDesc === '') || isLoadingName || isLoadingDesc
                    }
                    className='pointer-events-auto ml-auto w-fit h-10 min-w-10 disabled:opacity-50'
                    analyticsFeature='Add Content'
                    analyticsPage='Courses'
                  >
                    <AIIcon
                      className={`w-[20px] h-[20px] ${!((contentName === '' && contentDesc === '') || isLoadingName || isLoadingDesc) ? 'text-primary' : ''}`}
                    />
                  </Button>
                </div>
              </div>
            </div>

            {contentType === 'assignment' && (
              <div className='mb-4 flex flex-col w-full gap-3'>
                <StandardInputCheckbox
                  id='blocksDownstreamContent'
                  label='Block Downstream Content'
                  checked={blocksDownstreamContent}
                  onChange={() => setBlocksDownstreamContent(!blocksDownstreamContent)}
                />

                <StandardInputCheckbox
                  id='hasDueDate'
                  label='Set Due Date'
                  checked={dueDate !== null}
                  onChange={() => {
                    if (dueDate === null) {
                      const today = new Date();
                      today.setHours(23, 59, 0, 0);
                      setDueDate(today);
                    } else {
                      setDueDate(null);
                      setLockAssignment(false);
                    }
                  }}
                />

                {dueDate !== null && (
                  <StandardInputCheckbox
                    id='lockAssignment'
                    label='Lock After Due Date'
                    checked={lockAssignment}
                    onChange={() => setLockAssignment(!lockAssignment)}
                  />
                )}

                {dueDate !== null && (
                  <div className='flex flex-col gap-1'>
                    <label htmlFor='dueDate' className='text-sm font-medium'>
                      Due Date
                    </label>
                    <input
                      type='datetime-local'
                      id='dueDate'
                      className='rounded-lg border border-border_gray_200 bg-surface_gray_50 px-3 py-2'
                      value={formatDate(dueDate)}
                      onChange={(e) => {
                        const date = new Date(e.target.value);
                        // If user only picked a date (hours==0, minutes==0) set default time 23:59
                        if (date.getHours() === 0 && date.getMinutes() === 0) {
                          date.setHours(23, 59, 0, 0);
                        }
                        setDueDate(date);
                      }}
                      required
                    />
                  </div>
                )}
              </div>
            )}
            {contentType !== 'quiz' && (
              <div className='mb-4 flex flex-col w-full relative'>
                <div className='w-full flex flex-col relative'>
                  <StandardInputTextarea
                    id='contentDesc'
                    label={`Content Description`}
                    isRequired={contentType === 'resource'}
                    value={contentDesc}
                    onChange={handleDescChange}
                    rows={5}
                    disabled={isLoadingDesc}
                    isLoading={isLoadingDesc}
                  />
                  <div className='absolute top-0 pt-[32px] right-0 z-10 h-full flex items-center justify-center pointer-events-none'>
                    <Button
                      text={true}
                      onClick={() => adjustContentDetails(false, true)}
                      disabled={
                        (contentName === '' && contentDesc === '') || isLoadingName || isLoadingDesc
                      }
                      className='pointer-events-auto ml-auto w-fit h-10 min-w-10 disabled:opacity-50'
                      analyticsFeature='Add Content'
                      analyticsPage='Courses'
                    >
                      <AIIcon
                        className={`w-[20px] h-[20px] ${!((contentName === '' && contentDesc === '') || isLoadingName || isLoadingDesc) ? 'text-primary' : ''}`}
                      />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {contentType === 'quiz' && (
              <div className='mb-4 flex flex-col w-full'>
                <div className='flex flex-col gap-1 bg-surface_gray_200 rounded-lg p-3 border border-border_gray_300'>
                  <p className='text-base'>Ready to create your quiz?</p>
                  <p className='text-sm text-text_gray_900'>
                    Click &quot;Create Quiz&quot; to open the quiz editor where you can add
                    questions and configure settings.
                  </p>
                </div>
              </div>
            )}

            {(contentType === 'document' || contentType === 'assignment') && !isTypeLocked && (
              <div className='mb-4 flex flex-col w-full'>
                <StandardInputFile
                  key={
                    modalVisible
                      ? `${contentType}-file-${modalVisible ? 'open' : 'closed'}`
                      : `${contentType}-file-closed`
                  }
                  id={contentType === 'document' ? 'contentFile' : 'assignmentFile'}
                  label='Upload File'
                  isRequired={contentType === 'document' ? true : false}
                  accept={contentType === 'document' ? '.pdf' : '.pdf,.doc,.docx'}
                  onChange={handleDocumentChange}
                />
              </div>
            )}

            {(contentType === 'video' || contentType === 'resource') && (
              <div className='mb-4 flex flex-col w-full'>
                <StandardInputText
                  id='contentUrl'
                  label={contentType === 'video' ? 'Video URL' : 'External Link'}
                  isRequired={true}
                  value={contentUrl}
                  onChange={(e) => setContentUrl(e.target.value)}
                />
                {contentType === 'video' &&
                  contentUrl !== '' &&
                  !ActivityCreationService.isVideoURLValid(contentUrl ?? '') && (
                    <p className='text-sm text-failure'>
                      Invalid video url. Please check that the link is a valid youtube format
                    </p>
                  )}
                {contentType === 'resource' &&
                  contentUrl !== '' &&
                  !ActivityCreationService.isResourceURLValid(contentUrl ?? '') && (
                    <p className='text-sm text-failure'>
                      Invalid external url, please check your link for errors
                    </p>
                  )}
              </div>
            )}
          </div>

          <div className='w-full flex flex-col gap-2'>
            <div className='flex flex-row gap-8 py-2'>
              <Button
                onClick={handleSubmit}
                disabled={
                  contentName == '' ||
                  (contentType == 'video' &&
                    !ActivityCreationService.isVideoURLValid(contentUrl)) ||
                  (contentType == 'document' && !contentFile && !isTypeLocked) ||
                  (contentType == 'resource' &&
                    (contentDesc == '' ||
                      !ActivityCreationService.isResourceURLValid(contentUrl))) ||
                  isLoadingName ||
                  isLoadingDesc
                }
                analyticsFeature='Add Content'
                analyticsPage='Courses'
              >
                {isTypeLocked ? (
                  <SaveIcon className='w-[20px] h-[20px] mr-2' />
                ) : (
                  <AddIcon className='w-[24px] h-[24px] mr-2' />
                )}
                {contentType === 'quiz' && !isTypeLocked
                  ? 'Create Quiz'
                  : `${isTypeLocked ? 'Save' : 'Add'} ${contentType.charAt(0).toUpperCase() + contentType.slice(1)}`}
              </Button>
              <Button
                outline={true}
                onClick={handleClose}
                analyticsFeature='Add Content'
                analyticsPage='Courses'
              >
                <CancelIcon className='w-[20px] h-[20px] mr-2' /> Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalComponent>
  );
}

export default ContentCreatorWizard;
